import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
    Bell,
    Sparkles,
    BookOpen,
    Users,
    Bug,
    Megaphone,
    Pin,
    CheckCheck,
    Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NEWS_CATEGORIES = [
    { value: 'new_feature',      label: 'New Feature',      icon: Sparkles,  bg: 'bg-indigo-500', text: 'text-indigo-500' },
    { value: 'medical_resource', label: 'Medical Resource', icon: BookOpen,  bg: 'bg-emerald-500', text: 'text-emerald-500' },
    { value: 'community',        label: 'Community',        icon: Users,     bg: 'bg-violet-500', text: 'text-violet-500' },
    { value: 'bug_fix',          label: 'Bug Fix',          icon: Bug,       bg: 'bg-slate-500', text: 'text-slate-500' },
    { value: 'awareness',        label: 'Awareness',        icon: Megaphone, bg: 'bg-amber-500', text: 'text-amber-500' },
];

export const getCategoryMeta = (value) =>
    NEWS_CATEGORIES.find(c => c.value === value) || NEWS_CATEGORIES[0];

export default function News() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(null);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter ? { category: filter } : {};
            const { data } = await axios.get(`${API_URL}/api/news`, { params });
            setPosts(data || []);
        } catch (e) {
            toast.error('Failed to load news');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);

    const markAllRead = async () => {
        try {
            await axios.post(`${API_URL}/api/news/mark-all-read`);
            toast.success('Marked all as read');
            fetchPosts();
        } catch (e) {
            toast.error('Failed');
        }
    };

    const markRead = async (id) => {
        try {
            await axios.post(`${API_URL}/api/news/${id}/read`);
            setPosts(prev => prev.map(p => p.id === id ? { ...p, read: true } : p));
        } catch (e) { /* ignore */ }
    };

    const hasUnread = posts.some(p => !p.read);

    return (
        <div className="space-y-6" data-testid="news-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Bell className="w-7 h-7 text-indigo-500" />
                        News & Updates
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Product updates, awareness moments, and announcements from the team
                    </p>
                </div>
                {hasUnread && (
                    <Button variant="outline" onClick={markAllRead} data-testid="news-mark-all-read">
                        <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
                    </Button>
                )}
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => setFilter(null)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${filter === null ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                    data-testid="news-filter-all"
                >
                    All
                </button>
                {NEWS_CATEGORIES.map(c => (
                    <button
                        key={c.value}
                        type="button"
                        onClick={() => setFilter(c.value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${filter === c.value ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                        data-testid={`news-filter-${c.value}`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
            ) : posts.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <Bell className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p>No news yet. Check back soon.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {posts.map(p => {
                        const meta = getCategoryMeta(p.category);
                        const Icon = meta.icon;
                        return (
                            <Card
                                key={p.id}
                                className={`transition-colors ${!p.read ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/30 dark:bg-indigo-950/10' : ''}`}
                                onClick={() => !p.read && markRead(p.id)}
                                data-testid={`news-post-${p.id}`}
                            >
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge className={`${meta.bg} text-white text-xs`}>
                                                <Icon className="w-3 h-3 mr-1" />
                                                {meta.label}
                                            </Badge>
                                            {p.is_pinned && (
                                                <Badge variant="outline" className="text-xs">
                                                    <Pin className="w-3 h-3 mr-1" /> Pinned
                                                </Badge>
                                            )}
                                            {!p.read && (
                                                <span className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold" data-testid={`unread-badge-${p.id}`}>
                                                    • New
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-muted-foreground flex-shrink-0">
                                            {p.published_at && format(parseISO(p.published_at), 'MMM d, yyyy')}
                                        </span>
                                    </div>
                                    <CardTitle className="text-lg mt-2">{p.title}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{p.body}</p>
                                    {p.author_name && (
                                        <p className="text-xs text-muted-foreground mt-3">— {p.author_name}</p>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
